import moment from 'moment';

const intervalDateFormat = 'YYYY-MM-DD';
const monthFormat = 'MM.YYYY';
const timeFormat = 'HH:mm';
const intervalDateTimeFormat = `${intervalDateFormat} ${timeFormat}`;

const getSchedulePeriodMoment = (schedule = {}) =>
  moment(schedule.period?.startMonth || schedule.startMonth, monthFormat, true);

const getWorkScheduleServiceNameId = (schedule = {}) =>
  schedule.workScheduleServiceNameId ??
  schedule.serviceSetIds?.[0] ??
  schedule.serviceSets?.[0]?.id;

const isTemporaryClientId = (id) =>
  typeof id === 'number' && Number.isFinite(id) && id > 1000000000000;

const getValidWorkScheduleServiceNameId = (schedule = {}) => {
  const id = getWorkScheduleServiceNameId(schedule);

  if (id === undefined || id === null || id === '' || isTemporaryClientId(id)) {
    throw new Error(
      'Выберите существующий набор услуг. Созданные в форме наборы пока не сохраняются на сервере.'
    );
  }

  return id;
};

const getWorkSchedulePeriodId = (schedule = {}) =>
  schedule.workSchedulePeriodId ??
  schedule.periodId ??
  schedule.sourceScheduleId ??
  schedule.id;

const getValidWorkSchedulePeriodId = (schedule = {}) => {
  const periodId = Number(getWorkSchedulePeriodId(schedule));

  if (!Number.isFinite(periodId) || periodId <= 0) {
    throw new Error('Не удалось определить график по периоду');
  }

  return periodId;
};

const getIntervalDatePayload = ({ date, interval = {} }) => {
  const fromDate = moment(
    `${date} ${interval.from}`,
    intervalDateTimeFormat,
    true
  );
  const toDate = moment(`${date} ${interval.to}`, intervalDateTimeFormat, true);

  if (!fromDate.isValid() || !toDate.isValid()) {
    return null;
  }

  if (toDate.isSameOrBefore(fromDate)) {
    toDate.add(1, 'day');
  }

  return {
    fromDate: fromDate.format(intervalDateTimeFormat),
    toDate: toDate.format(intervalDateTimeFormat),
  };
};

const getWorkersPayload = (schedule = {}) =>
  Object.entries(schedule.workIntervals || {})
    .map(([workerId, intervalsByDate]) => {
      const interval = Object.entries(intervalsByDate || {})
        .map(([date, item]) => getIntervalDatePayload({ date, interval: item }))
        .filter(Boolean);

      return {
        workerId: Number(workerId),
        interval,
      };
    })
    .filter((worker) => worker.interval.length);

const getChildWorkScheduleHousesPayload = (schedule = {}) => {
  const allCompanyAddresses = Boolean(
    schedule.allCompanyAddresses ?? schedule.address?.allCompanyAddresses
  );

  if (allCompanyAddresses) {
    return {
      allCompanyAddresses,
      houses: {
        serviceCompanyId: Number(
          schedule.companyId ?? schedule.address?.companyId
        ),
      },
    };
  }

  const cityRegionIds = [
    ...(schedule.cityRegionIds ?? schedule.address?.cityRegionIds ?? []),
    schedule.cityRegionId ?? schedule.address?.cityRegionId,
  ]
    .map(Number)
    .filter(
      (id, index, values) =>
        Number.isFinite(id) &&
        id > 0 &&
        values.findIndex((value) => value === id) === index
    );
  const housesIds = (schedule.houseIds || schedule.address?.houseIds || []).map(
    Number
  );
  const houses = {};

  if (cityRegionIds.length) {
    houses.cityRegionsIds = cityRegionIds;
  }

  if (housesIds.length) {
    houses.housesIds = housesIds;
  }

  return {
    allCompanyAddresses,
    houses,
  };
};

const getChildWorkScheduleCityId = (schedule = {}) => {
  const cityIds = schedule.cityIds ?? schedule.address?.cityIds ?? [];

  return Number(
    schedule.cityId ??
      schedule.address?.cityId ??
      (cityIds.length === 1 ? cityIds[0] : 0)
  );
};

const assertChildWorkSchedulePayload = (
  payload = {},
  { allCompanyAddresses = false } = {}
) => {
  if (!Number.isFinite(payload.cityId) || payload.cityId <= 0) {
    throw new Error('Выберите город');
  }

  if (
    !allCompanyAddresses &&
    !payload.houses?.housesIds?.length &&
    !payload.houses?.cityRegionsIds?.length
  ) {
    throw new Error('Выберите микрорайон или минимум один дом');
  }

  if (
    allCompanyAddresses &&
    (!Number.isFinite(payload.houses?.serviceCompanyId) ||
      payload.houses.serviceCompanyId <= 0)
  ) {
    throw new Error('Выберите управляющую компанию');
  }

  if (!payload.workers?.length) {
    throw new Error('Добавьте минимум одного исполнителя с интервалом');
  }
};

export const getChildWorkScheduleCreatePayload = (schedule = {}) => {
  const scheduleMonth = getSchedulePeriodMoment(schedule);
  const { allCompanyAddresses, houses } =
    getChildWorkScheduleHousesPayload(schedule);
  const payload = {
    workScheduleId: Number(
      schedule.parentId ?? schedule.workScheduleId ?? schedule.parentScheduleId
    ),
    scheduleMonth: scheduleMonth.isValid()
      ? Number(scheduleMonth.format('M'))
      : undefined,
    scheduleYear: scheduleMonth.isValid()
      ? Number(scheduleMonth.format('YYYY'))
      : undefined,
    workScheduleServiceNameId: getValidWorkScheduleServiceNameId(schedule),
    cityId: getChildWorkScheduleCityId(schedule),
    houses,
    workers: getWorkersPayload(schedule),
  };

  assertChildWorkSchedulePayload(payload, { allCompanyAddresses });

  return payload;
};

export const getChildWorkScheduleServiceNameUpdatePayload = (
  schedule = {}
) => ({
  workSchedulePeriodId: getValidWorkSchedulePeriodId(schedule),
  workScheduleServiceNameId: Number(
    getValidWorkScheduleServiceNameId(schedule)
  ),
});

export const getChildWorkScheduleConfigurationPayload = (schedule = {}) => {
  const { allCompanyAddresses, houses } =
    getChildWorkScheduleHousesPayload(schedule);
  const payload = {
    periodId: getValidWorkSchedulePeriodId(schedule),
    cityId: getChildWorkScheduleCityId(schedule),
    houses,
    workers: getWorkersPayload(schedule),
  };

  assertChildWorkSchedulePayload(payload, { allCompanyAddresses });

  return payload;
};
